import React, { type Key } from 'react';

enum SearchActionType {
  SET_SEARCH_VALUE = 'SET_SEARCH_VALUE',
  SET_SEARCH_TYPE = 'SET_SEARCH_TYPE',
}

interface SearchState {
  searchValue: string;
  searchType?: string;
}

interface UseSearchTextFieldStateProps {
  defaultValue?: string;
  defaultSearchType?: string;
  onSearchByChange?: (key: Key | null) => void;
}

function searchReducer(state: SearchState, action: { type: SearchActionType; payload: string }): SearchState {
  switch (action.type) {
    case SearchActionType.SET_SEARCH_VALUE:
      return { ...state, searchValue: action.payload };
    case SearchActionType.SET_SEARCH_TYPE:
      return { ...state, searchType: action.payload, searchValue: '' }; // Reset the search value when the search type changes
    default:
      return state;
  }
}

/**
 * Custom hook to manage search text field state and handlers.
 * Provides state management for search value and search type selection with automatic
 * search value reset when the search type changes.
 *
 * @param props - Hook configuration options
 *
 * @example
 * const { state, onSearchValueChange, onSearchTypeChange } = useSearchTextFieldState({
 *   defaultValue: 'Initial search',
 *   defaultSearchType: 'name',
 *   onSearchByChange: (key) => console.log('Search type changed:', key)
 * });
 */
function useSearchTextFieldState(props: UseSearchTextFieldStateProps) {
  const { defaultValue = '', defaultSearchType = '', onSearchByChange } = props;

  const [state, dispatch] = React.useReducer(searchReducer, {
    searchValue: defaultValue,
    searchType: defaultSearchType,
  });

  const handleChange = (value: string) => {
    dispatch({ type: SearchActionType.SET_SEARCH_VALUE, payload: value });
  };

  const handleSearchTypeChange = (key: Key | null) => {
    if (key !== null) {
      dispatch({ type: SearchActionType.SET_SEARCH_TYPE, payload: String(key) });

      onSearchByChange?.(key);
    }
  };

  const handleClear = () => {
    dispatch({ type: SearchActionType.SET_SEARCH_VALUE, payload: '' });
  };

  return { state, onSearchValueChange: handleChange, onSearchTypeChange: handleSearchTypeChange, onClear: handleClear };
}

export { useSearchTextFieldState };
